import React, { useCallback, useEffect, useState } from "react";

import { Box, Grid, Paper, Skeleton } from "@mui/material";

import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

import { useMsal } from "@azure/msal-react";

import DashboardHeader from "../components/dashboard/DashboardHeader";
import StatCard from "../components/dashboard/StatCard";
import StatusChart from "../components/dashboard/StatusChart";
import PriorityChart from "../components/dashboard/PriorityChart";
import RecentTickets from "../components/dashboard/RecentTickets";
import AssignedProjects from "../components/dashboard/AssignedProjects";

import { getDashboardSummary } from "../services/dashboardService";
import { useUserRole } from "../context/UserRoleContext";
import { USER_ROLES } from "../auth/roles";

function DashboardSkeleton() {
  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "background.default",
        minHeight: "100vh",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Skeleton variant="text" width={320} height={44} />
          <Skeleton variant="text" width={430} height={26} />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {Array.from({ length: 7 }).map((_, index) => (
          <Paper
            key={`kpi-skeleton-${index}`}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              minHeight: 120,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Skeleton variant="text" width={90} height={24} />
                <Skeleton variant="text" width={70} height={46} />
              </Box>
              <Skeleton variant="circular" width={52} height={52} />
            </Box>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        {Array.from({ length: 2 }).map((_, index) => (
          <Paper
            key={`chart-skeleton-${index}`}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              minHeight: 380,
            }}
          >
            <Skeleton variant="text" width={230} height={34} />
            <Skeleton variant="rounded" width="100%" height={250} sx={{ mt: 1 }} />
            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Skeleton variant="text" width={70} />
              <Skeleton variant="text" width={70} />
              <Skeleton variant="text" width={70} />
            </Box>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          minHeight: 380,
        }}
      >
        <Skeleton variant="text" width={190} height={34} />
        <Skeleton variant="rounded" width="100%" height={290} sx={{ mt: 1.2 }} />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          minHeight: 240,
        }}
      >
        <Skeleton variant="text" width={220} height={34} />
        <Skeleton variant="rounded" width="100%" height={160} sx={{ mt: 1.2 }} />
      </Paper>
    </Box>
  );
}

function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const { accounts } = useMsal();
  const { role, employee, loading: roleLoading } = useUserRole();
  const loggedInUserName = accounts?.[0]?.name || "User";

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const employeeId = role === USER_ROLES.SUPER_ADMIN
        ? null
        : employee?.employee_id ?? -1;
      const data = await getDashboardSummary(employeeId);

      setDashboard(data);
    } catch (error) {
      console.error("Dashboard Error:", error);
    } finally {
      setLoading(false);
    }
  }, [role, employee]);

  useEffect(() => {
    if (roleLoading) {
      return;
    }

    loadDashboard();
  }, [loadDashboard, roleLoading]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!dashboard) {
    return null;
  }

  const summary = dashboard.summary;

  const kpiCards = [
    {
      title: "Projects",
      value: summary.total_projects,
      icon: <AssignmentIndIcon />,
      color: "#0ea5e9",
    },
    {
      title: "Total Tickets",
      value: summary.total_tickets,
      icon: <ConfirmationNumberIcon />,
      color: "#2563eb",
    },
    {
      title: "Open",
      value: summary.open_tickets,
      icon: <FolderOpenIcon />,
      color: "#f59e0b",
    },
    {
      title: "Assigned",
      value: summary.assigned_tickets,
      icon: <AssignmentIndIcon />,
      color: "#06b6d4",
    },
    {
      title: "Pending",
      value: summary.in_progress_tickets,
      icon: <AutorenewIcon />,
      color: "#7c3aed",
    },
    {
      title: "Done",
      value: summary.resolved_tickets,
      icon: <TaskAltIcon />,
      color: "#16a34a",
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "background.default",
        minHeight: "100vh",
      }}
    >
      <DashboardHeader userName={loggedInUserName} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {kpiCards.map((card) => (
          <StatCard
            key={card.title}
            title={card.title}
            value={card.value}
            icon={card.icon}
            color={card.color}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <StatusChart data={dashboard.status_distribution} />
        <PriorityChart data={dashboard.priority_distribution} />
      </Box>

      <Box sx={{ mt: 3 }}>
        <RecentTickets tickets={dashboard.recent_tickets} />
      </Box>

      <Grid container spacing={3} mt={0.5}>
        <Grid item xs={12}>
          <AssignedProjects projects={dashboard.assigned_projects || []} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
